import { UserProfileModel } from '@nw/fields-manager';
import {
  EmiSelectionType,
  GetProductDetailsResponseType,
  GetUserOrderReferralDiscountResponseType,
  InstallmentType,
  PricingType
} from '@nw/shared/data-access';

import {
  DefaultPricingDetails,
  DefaultProductDetail,
  EMIInstallmentPricingDetails,
  EMIInstallmentProductDetail,
  ExternalManagedInstallmentPricingDetails,
  ExternalManagedInstallmentProductDetail,
  FlexibleInstallmentPricingDetails,
  FlexibleInstallmentProductDetail,
  FlexibleTenureEMIPricingDetails,
  FlexibleTenureEMIProductDetail,
  PeriodicInstallmentPricingDetails,
  PeriodicInstallmentProductDetail
} from '../models';
import { AllProductTypes } from '../types/storesTypes';

export const createProductDetailModel = (
  productDetails: GetProductDetailsResponseType,
  userProfileDetails: UserProfileModel | null
) => {
  const { pricing_type } = productDetails.product_details;

  if (pricing_type === PricingType.enum.FULL_PAYMENT) {
    return new DefaultProductDetail(productDetails, userProfileDetails);
  }

  if (pricing_type === PricingType.enum.INSTALLMENT) {
    const installmentDetails =
      productDetails.product_details.installment_pricing_details;

    if (!installmentDetails) {
      throw new Error(
        `Installment details missing for product: ${productDetails.product_code}`
      );
    }

    const { installment_type, emi_selection } = installmentDetails;

    switch (installment_type) {
      case InstallmentType.enum.EMI:
        if (emi_selection === EmiSelectionType.enum.PRE_ORDER) {
          return new EMIInstallmentProductDetail(
            productDetails,
            userProfileDetails
          );
        } else if (emi_selection === EmiSelectionType.enum.POST_ORDER) {
          return new FlexibleTenureEMIProductDetail(
            productDetails,
            userProfileDetails
          );
        }
        throw new Error(
          `Unknown emi_selection: ${emi_selection} for product: ${productDetails.product_code}`
        );

      case InstallmentType.enum.CUSTOMIZABLE:
        return new FlexibleInstallmentProductDetail(
          productDetails,
          userProfileDetails
        );

      case InstallmentType.enum.PERIODIC:
        return new PeriodicInstallmentProductDetail(
          productDetails,
          userProfileDetails
        );

      case InstallmentType.enum.EXTERNAL_MANAGED:
        return new ExternalManagedInstallmentProductDetail(
          productDetails,
          userProfileDetails
        );

      default:
        throw new Error(
          `Unknown installment_type: ${installment_type} for product: ${productDetails.product_code}`
        );
    }
  }

  throw new Error(
    `Unknown pricing_type: ${pricing_type} for product: ${productDetails.product_code}`
  );
};

export const createProductWithUpdatedPricingDetailModel = (
  referralCodeResponse: GetUserOrderReferralDiscountResponseType,
  currentSelectedProduct: AllProductTypes
) => {
  switch (true) {
    case currentSelectedProduct instanceof DefaultProductDetail: {
      const newPricingDetails = new DefaultPricingDetails(
        referralCodeResponse as unknown as GetProductDetailsResponseType
      );
      currentSelectedProduct.updateCurrentDetailsWithReferralCodeResponse(
        newPricingDetails
      );
      return currentSelectedProduct;
    }
    case currentSelectedProduct instanceof EMIInstallmentProductDetail: {
      const newPricingDetails = new EMIInstallmentPricingDetails(
        referralCodeResponse as unknown as GetProductDetailsResponseType
      );
      currentSelectedProduct.updateCurrentDetailsWithReferralCodeResponse(
        newPricingDetails
      );
      return currentSelectedProduct;
    }
    case currentSelectedProduct instanceof FlexibleInstallmentProductDetail: {
      const newPricingDetails = new FlexibleInstallmentPricingDetails(
        referralCodeResponse as unknown as GetProductDetailsResponseType
      );
      currentSelectedProduct.updateCurrentDetailsWithReferralCodeResponse(
        newPricingDetails
      );
      return currentSelectedProduct;
    }
    case currentSelectedProduct instanceof ExternalManagedInstallmentProductDetail: {
      const newPricingDetails = new ExternalManagedInstallmentPricingDetails(
        referralCodeResponse as unknown as GetProductDetailsResponseType
      );
      currentSelectedProduct.updateCurrentDetailsWithReferralCodeResponse(
        newPricingDetails
      );
      return currentSelectedProduct;
    }
    case currentSelectedProduct instanceof FlexibleTenureEMIProductDetail: {
      const newPricingDetails = new FlexibleTenureEMIPricingDetails(
        referralCodeResponse as unknown as GetProductDetailsResponseType
      );
      currentSelectedProduct.updateCurrentDetailsWithReferralCodeResponse(
        newPricingDetails
      );
      return currentSelectedProduct;
    }
    case currentSelectedProduct instanceof PeriodicInstallmentProductDetail: {
      const newPricingDetails = new PeriodicInstallmentPricingDetails(
        referralCodeResponse as unknown as GetProductDetailsResponseType
      );
      currentSelectedProduct.updateCurrentDetailsWithReferralCodeResponse(
        newPricingDetails
      );
      return currentSelectedProduct;
    }
    default: {
      return null;
    }
  }
};
